import quickFetch from 'quick-fetch';
import { PromiseClient } from 'hub-http/adapters/promiseClient';
import { createRpcClientV2 } from 'rpc-client-utils';
import { CRM_SEARCH_QUERY } from './graphql';
import {
  HsEntitiesCrmSearch,
  HsEntitiesCrmSearchVariables,
} from '../../gql-types';
import { print } from 'graphql/language/printer';
import { ResolverHttpClient } from 'pulse/resolvers';
import { RpcError } from 'pulse/adapters';
import { handleGQLResult, consumeEarlyCrmSearch } from './early';
import {
  allPropertiesRequestName,
  buildSearchCrmObjectsRequest,
  consumeEarlyCrmSearchAllProperties,
  handleSalesViewsResult,
  handleSearchCrmObjectsResult,
  buildSalesViewsSearchBody,
  SALES_VIEWS_SEARCH_URL,
  type SalesViewsSearchResponse,
} from './earlyAllProperties';
import { searchCrmObjectsRpc } from '../../../__generated__/chirp/com/hubspot/sales/rpc/ViewServiceRpc';
import { drainSortDurations } from './sortPerfBuffer';
import {
  CrmSearchAllPropertiesArgs,
  CrmSearchErrors,
  CrmSearchResults,
  CrmObject,
} from './types';
import { Metrics } from '../../utils/metrics';
import { readUserIdFromAuth, type GetUserId } from './getUserId';
import { isSearchRpcGateEnabled } from './gate';

// consume/earlyRequest sort filterGroups themselves (for identity matching);
// this just drains and reports the buffered timings once per resolver call.
const reportSortMetrics = () => {
  drainSortDurations().forEach(({ durationMs, context }) => {
    Metrics.timer('crm_search_sort_ms', { context }).update(durationMs);
  });
};

// `args` are passed through unsorted: filterGroup sorting exists only to make
// the early-request identity key match a standard request, and the server is
// filter-order-agnostic, so the fallback fetch has no reason to sort.
const baseFetch = ({
  http,
  args,
}: {
  http: ResolverHttpClient;
  args: HsEntitiesCrmSearchVariables;
}): Promise<CrmSearchResults> =>
  http
    .post<{
      data: HsEntitiesCrmSearch;
      errors?: CrmSearchErrors;
    }>('/graphql/crm', {
      data: {
        query: print(CRM_SEARCH_QUERY),
        variables: args,
      },
    })
    .then((result) =>
      handleGQLResult(args.objectTypeId, result.data, result.errors)
    );

const baseFetchAllProperties = ({
  http,
  args,
  userId,
}: {
  http: ResolverHttpClient;
  args: CrmSearchAllPropertiesArgs;
  userId?: number;
}): Promise<CrmSearchResults<CrmObject>> =>
  http
    .post<SalesViewsSearchResponse>(SALES_VIEWS_SEARCH_URL, {
      data: buildSalesViewsSearchBody(args, userId),
    })
    .then(handleSalesViewsResult);

const searchCrmObjects = (
  http: ResolverHttpClient,
  args: CrmSearchAllPropertiesArgs,
  earlyRequestName?: string
): Promise<CrmSearchResults<CrmObject>> =>
  createRpcClientV2({ hubHttpClient: http as PromiseClient })
    .callWithResultUnion(searchCrmObjectsRpc, {
      request: buildSearchCrmObjectsRequest(args),
      earlyRequestName,
    })
    .then((result) => {
      if (result.type === 'rpcError') {
        throw new RpcError(result.rpcError);
      }
      return handleSearchCrmObjectsResult(result.data);
    });

const fetchAllPropertiesWithRpc = (
  http: ResolverHttpClient,
  args: CrmSearchAllPropertiesArgs
) => {
  const earlyRequestName = allPropertiesRequestName(args, 'standard');
  if (!quickFetch.getRequestStateByName(earlyRequestName)) {
    return searchCrmObjects(http, args);
  }
  return searchCrmObjects(http, args, earlyRequestName).catch(() =>
    searchCrmObjects(http, args)
  );
};

const fetchAllPropertiesWithRest = (
  http: ResolverHttpClient,
  args: CrmSearchAllPropertiesArgs,
  getUserId: GetUserId
) =>
  consumeEarlyCrmSearchAllProperties(args).catch(() =>
    getUserId()
      .catch(() => undefined)
      .then((userId) => baseFetchAllProperties({ http, args, userId }))
  );

export const makeCrmSearchRequest = (
  http: ResolverHttpClient,
  args: HsEntitiesCrmSearchVariables
) => {
  const result = consumeEarlyCrmSearch(args).catch(() =>
    baseFetch({ http, args })
  );
  reportSortMetrics();
  return result;
};

export const makeCrmSearchAllPropertiesRequest = (
  http: ResolverHttpClient,
  args: CrmSearchAllPropertiesArgs,
  getUserId: GetUserId = readUserIdFromAuth
) => {
  return isSearchRpcGateEnabled().then((enabled) => {
    const result = enabled
      ? fetchAllPropertiesWithRpc(http, args)
      : fetchAllPropertiesWithRest(http, args, getUserId);
    reportSortMetrics();
    return result;
  });
};
